import { Component, HostBinding } from '@angular/core';
import { CanDisable, mixinDisabled } from '@angular/material/core';
import * as _ from 'lodash';
import { combineLatest, concatMap, filter, Observable, tap } from 'rxjs';

import { AvailableFacadeCustomSlotSchema } from 'cloud-shared-lib';
import { CuttingFacadeSlot } from '../../../models/cutting/cutting-facade-slot.model';
import { GrooveTypeEnum } from '../../../models/cutting/cutting-panel-base-slot.model';
import { OutlineSide, PlateTypeEnum } from '../../../models/cutting/cutting-panel-outline.model';
import { Position } from '../../../models/cutting/cutting-svg.model';
import {
  FacadeAvailableSlot,
  FacadeSlotAvailablePlatesEnum,
} from '../../../models/cutting/facade-available-slot.model';
import { FacadeView } from '../../../models/cutting/facade-view.model';
import { LegacyDialogsService } from '../../../services/legacy-dialogs.service';
import { SlotsDialogComponent, SlotsDialogData } from '../../slots-dialog/slots-dialog.component';
import { FacadeSettingsDataService } from '../facade-settings-data.service';
import { FacadeSettingsStateService } from '../facade-settings-state.service';

export class SlotsBase {}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const _SlotsMixinBase = mixinDisabled(SlotsBase);

@Component({
  selector: 'csl-slots',
  templateUrl: './slots.component.html',
  styleUrls: ['./slots.component.scss'],
  // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
  inputs: ['disabled'],
})
export class SlotsComponent extends _SlotsMixinBase implements CanDisable {
  @HostBinding('class.facade-settings-slots') idClass = true;

  panel: FacadeView;

  action$: Observable<any>;
  data$: Observable<any>;

  constructor(
    private facadeSettingsService: FacadeSettingsStateService,
    private dialogs: LegacyDialogsService,
    private facadeSettingsDataService: FacadeSettingsDataService
  ) {
    super();

    this.data$ = facadeSettingsService.panel$.pipe(tap((p: FacadeView) => (this.panel = p)));
  }

  selectSlot(edge: Position): void {
    this.action$ = combineLatest([
      this.facadeSettingsDataService.getFacadeSlots(),
      this.facadeSettingsDataService.getAvailableCustomSlotsSchemas(null),
    ]).pipe(
      concatMap(([slots, customSlotSchemas]) =>
        this.dialogs.component<
          SlotsDialogData<AvailableFacadeCustomSlotSchema, FacadeAvailableSlot>,
          FacadeAvailableSlot
        >(SlotsDialogComponent, {
          slots,
          customSlotSchemas,
          postCustomSlotsData: this.facadeSettingsDataService.postCustomSlotsData,
          deleteCustomSlot: this.facadeSettingsDataService.deleteCustomSlot,
          mapSchemaToSlot: data => ({
            availablePlates: data.schema.availablePlates,
            custom: true,
            designation: data.designation,
            name: data.schema.name,
            b: data.bShift,
            g: data.depth,
            h: data.width,
            dx: data.shift,
            fi: data.angle,
            r: data.radius,
            id: 0,
            plate: PlateTypeEnum.Back,
            type: data.schema.type,
          }),
        })
      ),
      filter(r => !!r),
      tap((s: FacadeAvailableSlot) => {
        const installedSlot = { ...s, plate: this.getDefaultPlate(s.availablePlates) };

        switch (edge) {
          case 'tc':
            this.panel.panelOutline.topSide.groove = installedSlot;
            break;

          case 'rc':
            this.panel.panelOutline.rightSide.groove = installedSlot;
            break;

          case 'bc':
            this.panel.panelOutline.bottomSide.groove = installedSlot;
            break;

          case 'lc':
            this.panel.panelOutline.leftSide.groove = installedSlot;
            break;
        }

        this.facadeSettingsService.updatePanel(this.panel);
      })
    );
  }

  delete(side: OutlineSide<CuttingFacadeSlot>) {
    side.groove = null;
    this.facadeSettingsService.updatePanel(this.panel);
  }

  getDescription(slot: CuttingFacadeSlot): string {
    if (!slot) {
      return $localize`Выберите паз`;
    }

    return slot.designation;
  }

  getIconLink(type: GrooveTypeEnum): string {
    if (_.isNil(type)) {
      return '';
    }

    const iconPath = (name: string) => `./assets/images/slots/${name}.svg`;

    switch (type) {
      case GrooveTypeEnum.Chamfer:
        return iconPath('chamfer');

      case GrooveTypeEnum.Rabbet:
        return iconPath('rabbet');

      case GrooveTypeEnum.InPlate:
        return iconPath('in-plate');

      case GrooveTypeEnum.InSide:
        return iconPath('in-side');

      case GrooveTypeEnum.Fillet:
        return iconPath('fillet');

      case GrooveTypeEnum.Chamfer2:
        return iconPath('chamfer2');

      default:
        return '';
    }
  }

  togglePlate(item: CuttingFacadeSlot): void {
    item.plate = item.plate === PlateTypeEnum.Back ? PlateTypeEnum.Face : PlateTypeEnum.Back;

    this.facadeSettingsService.updatePanel(this.panel);
  }

  isDoubleSidedSlot(item: CuttingFacadeSlot): boolean {
    return item?.availablePlates === FacadeSlotAvailablePlatesEnum.FaceAndBack;
  }

  isBackPlate(item: CuttingFacadeSlot): boolean {
    return item?.plate === PlateTypeEnum.Back;
  }

  getPlateClass(slot: CuttingFacadeSlot): string {
    if (!slot) {
      return '';
    }

    switch (slot.plate) {
      case PlateTypeEnum.Back:
        return 'back-plate';

      case PlateTypeEnum.Face:
        return 'face-plate';

      case PlateTypeEnum.Edge:
        return 'edge-plate';

      default:
        return '';
    }
  }

  private getDefaultPlate(availablePlates: FacadeSlotAvailablePlatesEnum): PlateTypeEnum {
    if (_.isNil(availablePlates)) return PlateTypeEnum.Back;

    const plates = {
      [FacadeSlotAvailablePlatesEnum.Back]: PlateTypeEnum.Back,
      [FacadeSlotAvailablePlatesEnum.Face]: PlateTypeEnum.Face,
      [FacadeSlotAvailablePlatesEnum.Edge]: PlateTypeEnum.Edge,
      [FacadeSlotAvailablePlatesEnum.FaceAndBack]: PlateTypeEnum.Back,
    };

    return plates[availablePlates];
  }
}
